import React, { useCallback, useEffect, useState } from 'react';
import { CircleCheckBig, Clock3, FileWarning, Play, RefreshCw, RotateCcw, Trash2, UserCheck } from 'lucide-react';
import * as hr from '../api';
import Loader from '../components/Loader';
import { useHR } from '../HRContext';
import ConfirmDialog from '../components/ConfirmDialog';
import { DateInput } from '../components/Controls';
import { iso } from './attendance/shared';
import { dt, t12 } from './biometric/shared';

// Time & Attendance > Biometric Registrations > Punch Exceptions: what the punches could not settle on their own.
//   Held          the device time is doubtful (clock never set, or dated after it arrived): accept or discard
//   No clock-out  a past day with only a clock-in: correct it from Attendance Management (it becomes a correction)
//   Record kept   the employee already had a manual / leave record that day: the punches were noted, not applied
// Plus Apply Now and Re-apply a period (after a schedule or policy change).

export default function BiometricExceptions() {
  const { notify } = useHR();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const today = iso(new Date());
  const [range, setRange] = useState({ start: today, end: today });

  const load = useCallback(() => hr.getPunchExceptions().then(r => { setD(r); setError(''); setSel(new Set()); }).catch(e => setError(e.message)), []);
  useEffect(() => { load(); }, [load]);

  if (error && !d) return <div className="error">{error}</div>;
  if (!d) return <Loader title="Loading punch exceptions…" subtitle="Checking held punches and incomplete days" />;
  const can = d.can.review;
  const toggle = id => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOn = d.held.length > 0 && sel.size === d.held.length;

  const run = async (fn, ok) => {
    setBusy(true);
    try { notify((await fn()).message || ok); await load(); } catch (e) { notify(e.message, 'bad'); } finally { setBusy(false); }
  };
  const review = action => run(() => hr.reviewPunches({ ids: [...sel], action }));
  const answer = yes => { const c = confirm; setConfirm(null); if (yes) c.go(); };

  return (
    <div className="mdr mds ael bio bdv">
      <div className="mdr-top">
        <div>
          <h2>Punch Exceptions</h2>
          <p>Punches held for a doubtful device time, days with a clock-in but no clock-out, and days where a manual record was kept over the punches.</p>
        </div>
        <div className="mdr-top-actions no-print">
          <button className="mdr-btn" onClick={() => load().then(() => notify('Refreshed'))}><RefreshCw size={14} /> Refresh</button>
          {can && <button className="mds-run" disabled={busy} onClick={() => run(() => hr.processPunches())}><Play size={14} /> Apply Punches Now</button>}
        </div>
      </div>

      <div className="mdr-card bdv-section">
        <div className="bdv-section-head">
          <div><h3><Clock3 size={16} /> Held for Review <small className="muted">({d.held.length})</small></h3>
            <small className="muted">Kept out of attendance because the device time cannot be right. Accept if it is, discard if it is not.</small></div>
          {can && d.held.length > 0 && (
            <div className="bdv-tools">
              <button className="mdr-btn" disabled={!sel.size || busy} onClick={() => review('accept')}><CircleCheckBig size={13} /> Accept ({sel.size})</button>
              <button className="mdr-btn ael-danger-text" disabled={!sel.size || busy} onClick={() => setConfirm({ title: 'Discard punches', label: 'Discard',
                message: `Discard ${sel.size} punch(es)?`, detail: 'They stay on record, marked discarded, and never count for attendance.', go: () => review('discard') })}><Trash2 size={13} /> Discard</button>
            </div>
          )}
        </div>
        <div className="mdr-scroll">
          <table className="mdr-table mds-table ts-table">
            <thead><tr>{can && <th className="c"><input type="checkbox" checked={allOn} onChange={() => setSel(allOn ? new Set() : new Set(d.held.map(x => x.id)))} aria-label="Select all" /></th>}
              <th>Device Time</th><th>Arrived</th><th>PIN</th><th>Employee</th><th>Device</th><th>Why</th></tr></thead>
            <tbody>
              {d.held.map(x => (
                <tr key={x.id} className="mdr-row">
                  {can && <td className="c"><input type="checkbox" checked={sel.has(x.id)} onChange={() => toggle(x.id)} aria-label={`Select ${x.pin}`} /></td>}
                  <td className="mono nowrap"><b>{t12(x.punch_local)}</b></td><td className="nowrap"><small>{dt(x.received_at)}</small></td>
                  <td className="mono">{x.pin}</td><td className="nowrap">{x.name ? <>{x.name} <small className="muted">{x.employee_no}</small></> : <span className="muted">Unmatched</span>}</td>
                  <td className="nowrap">{x.device}</td><td><small>{x.reason}</small></td>
                </tr>
              ))}
              {!d.held.length && <tr><td colSpan={7} className="mdr-empty">No punches are held.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mdr-card bdv-section">
        <div className="bdv-section-head">
          <div><h3><FileWarning size={16} /> No Clock-Out <small className="muted">({d.missing_out.length}, last 62 days)</small></h3>
            <small className="muted">A past day with only a clock-in counts 0 hours. Edit it in Attendance Management › Daily Register (it goes through as a correction), or wait for a late punch to arrive.</small></div>
        </div>
        <div className="mdr-scroll">
          <table className="mdr-table mds-table ts-table">
            <thead><tr><th>Date</th><th>Employee</th><th>Clock-In</th></tr></thead>
            <tbody>
              {d.missing_out.map(x => (
                <tr key={x.record_id} className="mdr-row"><td className="nowrap">{x.date}</td><td className="nowrap">{x.name} <small className="muted">{x.employee_no}</small></td><td className="mono">{t12(x.time_in)}</td></tr>
              ))}
              {!d.missing_out.length && <tr><td colSpan={3} className="mdr-empty">Every past biometric day has a clock-out.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mdr-card bdv-section">
        <div className="bdv-section-head">
          <div><h3><UserCheck size={16} /> Record Kept <small className="muted">({d.kept.length}, last 62 days)</small></h3>
            <small className="muted">The employee already had a record for the day (entered by hand, imported, or approved leave). It was kept; the punches are only noted against it.</small></div>
        </div>
        <div className="mdr-scroll">
          <table className="mdr-table mds-table ts-table">
            <thead><tr><th>Date</th><th>Employee</th><th className="c">Punches</th><th>First</th><th>Last</th><th>Note</th></tr></thead>
            <tbody>
              {d.kept.map(x => (
                <tr key={`${x.employee_id}-${x.date}`} className="mdr-row"><td className="nowrap">{x.date}</td><td className="nowrap">{x.name} <small className="muted">{x.employee_no}</small></td>
                  <td className="c">{x.punches}</td><td className="mono">{t12(x.first)}</td><td className="mono">{t12(x.last)}</td><td><small>{x.note}</small></td></tr>
              ))}
              {!d.kept.length && <tr><td colSpan={6} className="mdr-empty">No conflicts with existing records.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {can && (
        <div className="mdr-card bdv-section">
          <div className="bdv-section-head">
            <div><h3><RotateCcw size={16} /> Re-apply a Period</h3>
              <small className="muted">After a shift, schedule or punch setting changes, re-date and re-apply that period's punches. Days HR has corrected, and manual or leave records, are left alone.</small></div>
          </div>
          <div className="bdv-tools">
            <label>From<DateInput value={range.start} onChange={e => setRange(r => ({ ...r, start: e.target.value }))} /></label>
            <label>To<DateInput value={range.end} onChange={e => setRange(r => ({ ...r, end: e.target.value }))} /></label>
            <button className="mds-run" disabled={busy || !range.start || !range.end} onClick={() => run(() => hr.reprocessPunches(range))}><RotateCcw size={13} /> Re-apply</button>
          </div>
        </div>
      )}

      {confirm && <ConfirmDialog danger title={confirm.title} confirmLabel={confirm.label} message={confirm.message} detail={confirm.detail} onAnswer={answer} />}
    </div>
  );
}
